import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

type RecentOrdersTableProps = {
  orders: any[];
  showComparison: boolean;
};

const RecentOrdersTable: React.FC<RecentOrdersTableProps> = ({ orders, showComparison }) => {
  // Sort orders by date in descending order (newest first)
  const sortedOrders = [...orders].sort((a, b) => 
    new Date(b.date_and_time).getTime() - new Date(a.date_and_time).getTime()
  );

  // Format date to NZ format with time
  const formatNZDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-NZ', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Recent Orders</CardTitle>
        <CardDescription>Last 10 orders received</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-secondary/50">
                  <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Order ID</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Customer</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Date</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Amount</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Type</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Net Income</th>
                  {showComparison && (
                    <>
                      <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Customer Savings</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Profit Increase</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y">
                {sortedOrders.slice(0, 10).map((order, index) => (
                  <tr key={index} className="hover:bg-muted/30 transition-colors">
                    <td className="px-4 py-3 text-sm">{order.pumpd_order_id || order.order_number || `#${index+1}`}</td>
                    <td className="px-4 py-3 text-sm">{order.customer_full_name || 'Customer'}</td>
                    <td className="px-4 py-3 text-sm">{formatNZDateTime(order.date_and_time)}</td>
                    <td className="px-4 py-3 text-sm">${parseFloat(order.cart_value).toFixed(2)}</td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        order.service_type === 'delivery' 
                          ? "bg-ubereats-green text-brand-white" 
                          : "bg-brand-yellow text-brand-dark-text"
                      }`}>
                        {order.service_type === 'delivery' ? 'Delivery' : 'Pickup'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm">${parseFloat(order.pumpd_net_income || 0).toFixed(2)}</td>
                    {showComparison && (
                      <>
                        <td className="px-4 py-3 text-sm">
                          {order.service_type === 'delivery' ? (
                            <span className="text-brand-yellow font-medium">
                              ${parseFloat(order.difference_in_cost_to_customer || 0).toFixed(2)}
                            </span>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {order.service_type === 'delivery' ? (
                            <span className="text-brand-blue font-medium">
                              ${parseFloat(order.difference_in_restaurant_income || 0).toFixed(2)}
                            </span>
                          ) : (
                            '—'
                          )}
                        </td>
                      </>
                    )}
                  </tr>
                ))}
                {orders.length === 0 && (
                  <tr>
                    <td colSpan={showComparison ? 8 : 6} className="px-4 py-6 text-center text-muted-foreground">
                      No orders found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default RecentOrdersTable;
